
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Tooltip,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Badge,
  Chip,
  TablePagination,
  Alert,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteIcon from '@mui/icons-material/Delete';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { PriceListItem } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';

interface HeadCell {
  id: keyof PriceListItem | 'action' | 'select';
  label: string;
}

const PriceList: React.FC = () => {
  const { canDeletePriceList } = usePermissions();
  const { canShowVendor } = usePermissions()

  const [priceList, setPriceList] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof PriceListItem>('productName');
  const [search, setSearch] = useState('');
  const [showVendor, setShowVendor] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Bulk delete states
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);


  const getHeadCells = (showVendor: boolean): HeadCell[] => {
    const cells: HeadCell[] = [
      { id: 'select', label: '' },
      { id: 'partNo', label: 'Part No' },
      { id: 'productName', label: 'Product Name' },
    ];

    // Conditionally add vendor column
    if (showVendor) {
      cells.push({ id: 'vendorName', label: 'Vendor' });
    }

    cells.push(
      { id: 'productBrand', label: 'Brand' },
      { id: 'carBrand', label: 'Car Brand' },
      { id: 'carModel', label: 'Car Model' },
      { id: 'mrp', label: 'MRP' },
      { id: 'action', label: 'Actions' }
    );

    return cells;
  };

  const headCells = getHeadCells(showVendor);

  /*const headCells: HeadCell[] = [
    { id: 'select', label: '' },
    { id: 'partNo', label: 'Part No' },
    { id: 'productName', label: 'Product Name' },
    { id: 'vendorName', label: 'Vendor' },
    { id: 'productBrand', label: 'Brand' },
    { id: 'carBrand', label: 'Car Brand' },
    { id: 'carModel', label: 'Car Model' },
    { id: 'mrp', label: 'MRP' },
    { id: 'action', label: 'Actions' }
  ];*/


  useEffect(() => {
    const fetchPriceList = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (debouncedSearch) params.append('search', debouncedSearch);
        params.append('page', (page + 1).toString());
        params.append('limit', rowsPerPage.toString());

        const url = `priceList/get-all-pricelist?${params.toString()}`;
        const res = await request({
          url: url,
          method: "get",
        });

        if (res.success) {
          const processedData = (res.data || []).map((item: PriceListItem) => ({
            ...item,
            description: processDescription(item.description)
          }));
          setPriceList(processedData);
          setTotal(res.pagination?.total || 0);
        }
      } catch (error: any) {
        console.error('Error fetching price list:', error);
        if (error?.response?.status !== 404) {
          toast.error(error || 'Failed to load price list');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPriceList();
  }, [debouncedSearch, page, rowsPerPage]);

  // Helper function to process description data
  const processDescription = (description: any) => {
    if (!description) {
      return { text: '', jsonFields: {} };
    }
    if (typeof description === 'string') {
      return { text: description, jsonFields: {} };
    }
    if (typeof description === 'object') {
      return {
        text: description.text || '',
        jsonFields: description.jsonFields || {}
      };
    }
    return { text: '', jsonFields: {} };
  };

  // Handle select all
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageIds = sortedPriceList.map((item) => item._id);
      setSelectedItems(currentPageIds);
    } else {
      setSelectedItems([]);
    }
  };

  // Handle select single item
  const handleSelectOne = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Check if all items on current page are selected
  const isAllSelectedOnPage = () => {
    if (sortedPriceList.length === 0) return false;
    const currentPageIds = sortedPriceList.map((item) => item._id);
    return currentPageIds.every((id) => selectedItems.includes(id));
  };

  // Check if some items are selected
  const isSomeSelected = () => {
    return selectedItems.length > 0 && selectedItems.length < sortedPriceList.length;
  };

  // Bulk delete API call
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    setBulkDeleting(true);
    try {
      const response = await request({
        url: 'priceList/bulk-delete-pricelist',
        method: 'delete',
        data: { ids: selectedItems }
      });

      if (response.success) {
        toast.success(response.message || `${selectedItems.length} item(s) deleted successfully`);
        setSelectedItems([]);
        setBulkDeleteDialogOpen(false);
        // Refresh the list
        const params = new URLSearchParams();
        if (debouncedSearch) params.append('search', debouncedSearch);
        params.append('page', (page + 1).toString());
        params.append('limit', rowsPerPage.toString());
        const url = `priceList/get-all-pricelist?${params.toString()}`;
        const res = await request({ url, method: "get" });
        if (res.success) {
          const processedData = (res.data || []).map((item: PriceListItem) => ({
            ...item,
            description: processDescription(item.description)
          }));
          setPriceList(processedData);
          setTotal(res.pagination?.total || 0);
        }
      } else {
        toast.error(response.message || 'Failed to delete selected items');
      }
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      toast.error(error?.message || 'Failed to delete selected items');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSort = (property: HeadCell['id']) => {
    if (property === 'action' || property === 'select') return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof PriceListItem);
  };

  const sortedPriceList = [...priceList].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue === undefined && bValue === undefined) return 0;
    if (aValue === undefined) return order === 'asc' ? 1 : -1;
    if (bValue === undefined) return order === 'asc' ? -1 : 1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSelectedItems([]); // Clear selection when changing page
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelectedItems([]); // Clear selection when changing rows per page
  };

  const handleViewEdit = (item: PriceListItem) => {
    navigate(`/vendors/priceList/${item._id}`);
  };

  const deletePricelist = async (pricelist: PriceListItem) => {
    try {
      const res = await request({
        url: `priceList/delete-pricelist/${pricelist._id}`,
        method: "delete",
      });

      if (res?.success) {
        toast.success(res?.message || 'Price list item deleted successfully');
        setPriceList((prev) => prev.filter((p) => p._id !== pricelist._id));
        setSelectedItems((prev) => prev.filter((id) => id !== pricelist._id));
      } else {
        toast.error(res?.message || 'Failed to delete price list item');
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<PriceListItem>({
    onDelete: deletePricelist,
  });

  const handleAddPriceList = () => {
    navigate('/vendors/priceList/addPriceList');
  };

  const handleUploadPriceList = () => {
    navigate('/vendors/pricelist/uploadPriceList');
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#00C389' : '#FF4D6D';
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleShowVendor = (e: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setShowVendor(e.target.checked)
  }

  // Truncated text component with tooltip
  const TruncatedText = ({ text, maxWidth = '120px' }: { text: string | undefined, maxWidth?: string }) => {
    if (!text) return <Typography variant="body2">-</Typography>;

    return (
      <Tooltip title={text} arrow placement="top">
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: maxWidth,
            cursor: 'help'
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Brandcrumb />
      </Box>

      <Card elevation={4}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                size='small'
                color="primary"
                onClick={handleAddPriceList}
                startIcon={<AddIcon />}
              >
                Add Price List
              </Button>

              <Button
                variant="outlined"
                size='small'
                color="primary"
                onClick={handleUploadPriceList}
                startIcon={<CloudUploadIcon />}
              >
                Upload Price List
              </Button>
            </Box>

            <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
              {canShowVendor() && <FormControlLabel control={<Switch checked={showVendor} onChange={handleShowVendor} />} label="Show Vendor" />}

              <Badge
                badgeContent={total}
                showZero
                color="primary"
              >
                <RequestQuoteIcon color="action" />
              </Badge>

              <TextField
                size="small"
                placeholder="Search by Part No, Product, Vendor, Brand, Description..."
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Bulk Action Bar */}
          {selectedItems.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'primary.light',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Typography variant="body2" fontWeight={500} color="primary.contrastText">
                {selectedItems.length} item(s) selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete Selected
              </Button>
            </Box>
          )}
        </CardContent>

        <CardContent sx={{ pt: 1, pb: 0 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((head) => (
                    <TableCell
                      key={head.id}
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: head.id === 'productName' ? '150px' :
                          head.id === 'vendorName' ? '120px' :
                            head.id === 'action' ? '140px' :
                              head.id === 'select' ? '40px' : 'auto',
                        py: 1,
                        fontSize: '0.75rem'
                      }}
                    >
                      {head.id === 'select' ? (
                        <Checkbox
                          size="small"
                          checked={isAllSelectedOnPage()}
                          indeterminate={isSomeSelected()}
                          onChange={handleSelectAll}
                          disabled={sortedPriceList.length === 0}
                        />
                      ) : head.id !== 'action' ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center" sx={{ py: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {debouncedSearch ? 'Searching...' : 'Loading price list...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedPriceList.length > 0 ? (
                  sortedPriceList.map((item) => (
                    <TableRow key={item._id} hover sx={{ height: '25px' }}>
                      {/* Checkbox */}
                      <TableCell sx={{ py: 1, px: 1 }}>
                        <Checkbox
                          size="small"
                          checked={selectedItems.includes(item._id)}
                          onChange={() => handleSelectOne(item._id)}
                        />
                      </TableCell>

                      {/* Part No with Status Dot */}
                      <TableCell sx={{ py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title={item.status === 'active' ? 'Active' : 'Inactive'}>
                            <FiberManualRecordIcon
                              sx={{
                                fontSize: 10,
                                color: getStatusColor(item.status)
                              }}
                            />
                          </Tooltip>
                          <TruncatedText text={item.partNo} maxWidth="100px" />
                        </Box>
                      </TableCell>

                      {/* Product Name */}
                      <TableCell sx={{ py: 1 }}>
                        <TruncatedText text={item.productName} maxWidth="150px" />
                      </TableCell>

                      {/* Vendor Name */}
                      {showVendor && <TableCell sx={{ py: 1 }}>
                        <TruncatedText text={item.vendorName} maxWidth="120px" />
                      </TableCell>}


                      {/* Product Brand */}
                      <TableCell sx={{ py: 1 }}>
                        {item.productBrand ? (
                          <Tooltip title={item.productBrand} arrow>
                            <Chip
                              label={item.productBrand}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: '24px',
                                maxWidth: '100px',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }
                              }}
                            />
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {/* Car Brand */}
                      <TableCell sx={{ py: 1 }}>
                        <TruncatedText text={item.carBrand} maxWidth="120px" />
                      </TableCell>

                      {/* Car Model */}
                      <TableCell sx={{ py: 1 }}>
                        <TruncatedText text={item.carModel} maxWidth="120px" />
                      </TableCell>

                      {/* MRP */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="primary"
                          sx={{ textAlign: 'left' }}
                        >
                          {formatCurrency(item.mrp)}
                        </Typography>
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell sx={{ py: 1 }}>
                        <Box display="flex" flexDirection="column" gap={0.8}>
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/inventory/addProduct`, {
                              state: {
                                source: 'price-list',
                                pricelistItemId: item._id
                              }
                            })}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              textWrap: 'nowrap',
                              py: 0.1,
                              px: 1,
                              '&:hover': { backgroundColor: '#222' }
                            }}
                          >
                            Move to Inventory
                          </Button>
                          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={0.8}>
                            <Button
                              variant="text"
                              onClick={() => handleViewEdit(item)}
                              fullWidth
                              sx={{
                                minWidth: 'auto',
                                fontSize: '0.60rem',
                                textWrap: 'nowrap',
                                py: 0.1,
                                px: 1.5,
                                borderRadius: 5,
                                border: '.8px solid #7f5ee666',
                                '&:hover': { backgroundColor: '#7f5ee666' }
                              }}
                            >
                              View / Edit
                            </Button>
                            {canDeletePriceList() && (
                              <Button
                                size="small"
                                fullWidth
                                variant="text"
                                color="error"
                                onClick={() => openDialog(item)}
                                sx={{
                                  minWidth: 'auto',
                                  fontSize: '0.60rem',
                                  py: 0.1,
                                  px: 1.5,
                                  borderRadius: 5,
                                  border: '.8px solid #ff4d6e5e',
                                  '&:hover': { backgroundColor: '#ff4d6e5e' }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <RequestQuoteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {search ? `No price list items found for "${search}"` : 'No price list items available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {search ? 'Try adjusting your search terms' : 'Get started by adding your first price list item'}
                        </Typography>
                        {!search && (
                          <Box display="flex" gap={1} justifyContent="center" sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              onClick={handleAddPriceList}
                              startIcon={<AddIcon />}
                            >
                              Add Manually
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleUploadPriceList}
                              startIcon={<CloudUploadIcon />}
                            >
                              Upload File
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '.MuiTablePagination-toolbar': {
                minHeight: '52px',
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Single Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title="Delete Pricelist"
        description={
          <>
            Are you sure you want to delete the price list item <strong>"{itemToDelete?.productName}"</strong>
            {itemToDelete?.partNo && ` (Part No: ${itemToDelete.partNo})`}? <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={bulkDeleteDialogOpen}
        loading={bulkDeleting}
        title="Delete Multiple Items"
        description={
          <>
            Are you sure you want to delete <strong>{selectedItems.length}</strong> price list item(s)?
            <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
              This action cannot be undone. All selected price list items and their associated data will be permanently removed.
            </Alert>
          </>
        }
        onCancel={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </>
  );
};

export default PriceList;